import { useState, type FormEvent } from 'react';
import { api, ApiError } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

// Validador de RFC cliente (espejo del servidor; se re-valida en API)
function validarRFC(raw: string): boolean {
  const r = raw.trim().toUpperCase();
  if (r === 'XAXX010101000' || r === 'XEXX010101000') return true;
  if (r.length === 12) return /^[A-ZÑ&]{3}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[A-Z0-9]{3}$/.test(r);
  if (r.length === 13) return /^[A-ZÑ&]{4}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[A-Z0-9]{3}$/.test(r);
  return false;
}

export interface ProveedorFormValues {
  rfc: string;
  razon_social: string;
  nombre_comercial?: string;
  tipo_proveedor: string;
  email?: string;
  telefono?: string;
  giro_comercial?: string;
  tamano_empresa?: string;
  domicilio?: string;
  representante_legal?: string;
  pagina_web?: string;
  regimen_fiscal?: string;
  codigo_postal_fiscal?: string;
  uso_cfdi_preferido?: string;
  clabe?: string;
  banco?: string;
  notas?: string;
}

export function ProveedorFormModal({
  open, onClose, onCreated, initial, id,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  initial?: Partial<ProveedorFormValues>;
  id?: string;
}) {
  const [values, setValues] = useState<ProveedorFormValues>({
    rfc: initial?.rfc ?? '',
    razon_social: initial?.razon_social ?? '',
    nombre_comercial: initial?.nombre_comercial ?? '',
    tipo_proveedor: initial?.tipo_proveedor ?? 'general',
    email: initial?.email ?? '',
    telefono: initial?.telefono ?? '',
    giro_comercial: initial?.giro_comercial ?? '',
    tamano_empresa: initial?.tamano_empresa ?? '',
    domicilio: initial?.domicilio ?? '',
    representante_legal: initial?.representante_legal ?? '',
    pagina_web: initial?.pagina_web ?? '',
    regimen_fiscal: initial?.regimen_fiscal ?? '',
    codigo_postal_fiscal: initial?.codigo_postal_fiscal ?? '',
    uso_cfdi_preferido: initial?.uso_cfdi_preferido ?? '',
    clabe: initial?.clabe ?? '',
    banco: initial?.banco ?? '',
    notas: initial?.notas ?? '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const rfcValido = values.rfc === '' || validarRFC(values.rfc);

  function set<K extends keyof ProveedorFormValues>(k: K, v: ProveedorFormValues[K]) {
    setValues((prev) => ({ ...prev, [k]: v }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!rfcValido) {
      setServerError('RFC inválido');
      return;
    }
    setSubmitting(true);
    setServerError(null);
    try {
      if (id) {
        await api(`/proveedores/${id}`, { method: 'PATCH', body: values });
      } else {
        await api('/proveedores', { method: 'POST', body: values });
      }
      onCreated();
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : 'No se pudo guardar');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={id ? 'Editar proveedor' : 'Nuevo proveedor'}
      subtitle={id ? undefined : 'Los datos fiscales se validan contra formato SAT (RFC, CURP, CLABE).'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">Cancelar</Button>
          <Button type="submit" form="proveedor-form" loading={submitting}>
            {id ? 'Guardar cambios' : 'Crear proveedor'}
          </Button>
        </>
      }
    >
      <form id="proveedor-form" onSubmit={submit} className="flex flex-col gap-5">
        {serverError && (
          <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700 dark:bg-rose-900/40 dark:text-rose-200">
            {serverError}
          </div>
        )}

        <section>
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">Datos fiscales</h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              name="rfc"
              label="RFC"
              required
              value={values.rfc}
              onChange={(e) => set('rfc', e.target.value.toUpperCase())}
              error={values.rfc !== '' && !rfcValido ? 'Formato SAT no válido' : undefined}
              hint="12 caracteres persona moral, 13 física"
              autoComplete="off"
            />
            <Input
              name="razon_social"
              label="Razón social"
              required
              value={values.razon_social}
              onChange={(e) => set('razon_social', e.target.value)}
            />
            <Input
              name="nombre_comercial"
              label="Nombre comercial"
              value={values.nombre_comercial}
              onChange={(e) => set('nombre_comercial', e.target.value)}
            />
            <div>
              <label className="text-[13px] font-medium text-ink">Tipo de proveedor</label>
              <select
                value={values.tipo_proveedor}
                onChange={(e) => set('tipo_proveedor', e.target.value)}
                className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle"
              >
                <option value="general">General</option>
                <option value="servicios">Servicios</option>
                <option value="materiales">Materiales</option>
                <option value="construccion">Construcción</option>
                <option value="tecnologia">Tecnología</option>
                <option value="consultoria">Consultoría</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <Input
              name="regimen_fiscal"
              label="Régimen fiscal SAT"
              hint="601, 612, 626, etc."
              value={values.regimen_fiscal}
              onChange={(e) => set('regimen_fiscal', e.target.value)}
            />
            <Input
              name="codigo_postal_fiscal"
              label="CP fiscal"
              value={values.codigo_postal_fiscal}
              onChange={(e) => set('codigo_postal_fiscal', e.target.value)}
            />
            <Input
              name="uso_cfdi_preferido"
              label="Uso CFDI preferido"
              hint="G03, D01, etc."
              value={values.uso_cfdi_preferido}
              onChange={(e) => set('uso_cfdi_preferido', e.target.value)}
            />
            <div>
              <label className="text-[13px] font-medium text-ink">Tamaño</label>
              <select
                value={values.tamano_empresa ?? ''}
                onChange={(e) => set('tamano_empresa', e.target.value)}
                className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle"
              >
                <option value="">(sin especificar)</option>
                <option value="micro">Micro</option>
                <option value="pequena">Pequeña</option>
                <option value="mediana">Mediana</option>
                <option value="grande">Grande</option>
              </select>
            </div>
          </div>
        </section>

        <section>
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">Contacto</h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input name="email" type="email" label="Email" value={values.email} onChange={(e) => set('email', e.target.value)} />
            <Input name="telefono" label="Teléfono" value={values.telefono} onChange={(e) => set('telefono', e.target.value)} />
            <Input name="pagina_web" label="Página web" value={values.pagina_web} onChange={(e) => set('pagina_web', e.target.value)} />
            <Input name="representante_legal" label="Representante legal" value={values.representante_legal} onChange={(e) => set('representante_legal', e.target.value)} />
            <Input name="domicilio" label="Domicilio" className="sm:col-span-2" value={values.domicilio} onChange={(e) => set('domicilio', e.target.value)} />
            <Input name="giro_comercial" label="Giro comercial" className="sm:col-span-2" value={values.giro_comercial} onChange={(e) => set('giro_comercial', e.target.value)} />
          </div>
        </section>

        <section>
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">Datos bancarios</h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              name="clabe"
              label="CLABE"
              hint="18 dígitos (se valida dígito verificador)"
              value={values.clabe}
              onChange={(e) => set('clabe', e.target.value)}
            />
            <Input name="banco" label="Banco" value={values.banco} onChange={(e) => set('banco', e.target.value)} />
          </div>
        </section>

        <section>
          <Input name="notas" label="Notas internas" value={values.notas} onChange={(e) => set('notas', e.target.value)} />
        </section>
      </form>
    </Modal>
  );
}
